import { GeoJSONSource, type Map as MlMap } from 'maplibre-gl'
import type { Alert, Severity } from '../../lib/types'
import { TONE_HEX } from '../../cc/tokens'

/**
 * The one replay in progress' own alerts -- a discrete marker layer, deliberately
 * separate from `layers/heatmap.ts`'s `eventFeatures`/`SRC_EVENTS`. That layer is an
 * always-on live density kernel over every current alert in the store, unrelated to
 * whatever trip is being replayed; this is the opposite shape -- a handful of exact
 * points, scoped to the one vehicle and time window `TripReplay.tsx`'s own
 * `tripReplayAlerts` already filtered down to, so a supervisor reviewing a trip sees
 * what actually happened on it rather than everything currently live on the corridor.
 *
 * Shape mirrors `layers/track.ts` exactly: SRC_ and LYR_ consts, a pure feature-builder
 * function, an add-layers function, and a set-data function.
 */

export const SRC_REPLAY_ALERTS = 'replay-alerts'
export const LYR_REPLAY_ALERTS = 'replay-alerts-dot'

const empty: GeoJSON.FeatureCollection<GeoJSON.Point> = { type: 'FeatureCollection', features: [] }

/** Same colour triple every severity chip in this console uses -- cc/tokens.ts's
 *  TONE_HEX, imported rather than duplicated (it's a plain hex-string map with no
 *  React/Tailwind dependency of its own) so a token change can't silently diverge the
 *  map dot from the timeline dot (TripReplay.tsx's own ALERT_SEV_COLOR uses the same
 *  import). */
const SEVERITY_COLOR: Record<Severity, string> = {
  CRITICAL: TONE_HEX.danger,
  WARN: TONE_HEX.warn,
  INFO: TONE_HEX.info,
}

/** Alerts without coordinates are dropped rather than placed at a fallback position --
 *  same guard `eventFeatures` (layers/heatmap.ts) uses for the live heatmap, for the same
 *  reason: an alert with no lat/lon has nowhere honest to be plotted.
 *
 *  `selectedAlertId`: the one alert a reader has picked (Reports > Alerts' trip drawer,
 *  map/TripMap.tsx) -- carried as a plain `selected` boolean property so the layer's
 *  paint expressions below can grow that dot without a feature-state round trip.
 *  Defaults to null, so MapCanvas.tsx's live replay (which has no notion of a picked
 *  alert) produces exactly the features it always did. */
export function replayAlertFeatures(
  alerts: readonly Alert[],
  selectedAlertId: string | null = null,
): GeoJSON.FeatureCollection<GeoJSON.Point> {
  return {
    type: 'FeatureCollection',
    features: alerts
      .filter((a) => a.lat != null && a.lon != null)
      .map((a) => ({
        type: 'Feature',
        id: a.alert_id,
        geometry: { type: 'Point', coordinates: [a.lon as number, a.lat as number] },
        properties: {
          alert_id: a.alert_id,
          kind: a.kind,
          severity: a.severity,
          color: SEVERITY_COLOR[a.severity] ?? SEVERITY_COLOR.INFO,
          message: a.message,
          ts: a.ts,
          // Set only when tripReplayAlerts folded a same-leg burst into this one alert
          // (collapseSameLegAlerts) -- carried through so a map popup built on this
          // source isn't missing the "N events" context the timeline dot's title shows.
          group_count: a._groupCount ?? null,
          group_first_ts: a._groupFirstTs ?? null,
          selected: selectedAlertId != null && a.alert_id === selectedAlertId,
        },
      })),
  }
}

/** `before`: same optional anchor `addHeatLayers` takes -- without it this layer (added
 *  after vehicles/heat/track) painted OVER the replayed truck icon whenever the playhead
 *  sat on an alert. MapCanvas.tsx passes the vehicle layer so alert dots sit under it. */
export function addReplayAlertLayers(map: MlMap, before?: string): void {
  const anchor = before && map.getLayer(before) ? before : undefined
  if (!map.getSource(SRC_REPLAY_ALERTS)) map.addSource(SRC_REPLAY_ALERTS, { type: 'geojson', data: empty })
  if (!map.getLayer(LYR_REPLAY_ALERTS)) {
    map.addLayer({
      id: LYR_REPLAY_ALERTS,
      type: 'circle',
      source: SRC_REPLAY_ALERTS,
      paint: {
        // The `selected` branch only ever fires for a TripMap-driven source (see
        // replayAlertFeatures); every live-replay dot evaluates to the old 7 / 1.5.
        'circle-radius': ['case', ['boolean', ['get', 'selected'], false], 11, 7],
        'circle-color': ['get', 'color'],
        'circle-stroke-color': ['case', ['boolean', ['get', 'selected'], false], '#FFFFFF', '#0F172A'],
        'circle-stroke-width': ['case', ['boolean', ['get', 'selected'], false], 3, 1.5],
      },
    }, anchor)
  }
}

export function setReplayAlertData(
  map: MlMap,
  alerts: readonly Alert[] | null,
  selectedAlertId: string | null = null,
): void {
  const src = map.getSource(SRC_REPLAY_ALERTS)
  if (src instanceof GeoJSONSource) src.setData(replayAlertFeatures(alerts ?? [], selectedAlertId))
}
